import React from 'react';
import {
    Alert,
    AlertTitle,
    Avatar,
    Button,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    TextField,
    Typography
} from "@mui/material";
import FolderIcon from '@mui/icons-material/Folder';
import {documentUploadApi} from "../../backend/DocumentUploadApi";
import LoaderWithCompanySelected from "../../components/LoaderWithCompanySelected";
import {logEvent} from "../../utils/SentryUtils";
import {DropzoneArea} from "react-mui-dropzone";
import {MonthPicker, YearPicker} from "@mui/lab";
import moment, {Moment} from "moment";
import {Trans, Translation} from "react-i18next";
import {format} from "../../utils/DataFormat";
import FileUploadProgress, {FileUploadProgressHandle} from "../../components/file/FileUploadProgress";
import {sleep} from "../../utils/ThreadUtils";

interface IProps {
}

interface IState {
    files: File[],
    rejectedFiles: Set<string>,
    activeStep: number,
    maxDate: Moment,
    minDate: Moment,
    selectedDate: Moment,
    comment?: string,

    uploadFinished: boolean,
}

export default class DocumentsUpload extends React.Component<IProps, IState> {

    private progressComponentRef = React.createRef<FileUploadProgressHandle>();

    constructor(props: IProps) {
        super(props);
        this.state = this.getInitialState()
    }

    private getInitialState = () => {
        const date = moment().startOf('month').subtract(1, 'month')
        return {
            files: [],
            rejectedFiles: new Set<string>(),
            activeStep: 0,
            minDate: date.clone().startOf('month').subtract(1, 'month'),
            maxDate: date.clone().endOf('day').add(1, 'month'),
            selectedDate: date,
            comment: undefined,

            uploadFinished: false
        };
    }

    private handleNext = () => {
        this.setState({activeStep: this.state.activeStep + 1});
    };

    private handleBack = () => {
        this.setState({activeStep: this.state.activeStep - 1});
    };

    private handleUploadStart = async () => {
        this.handleNext();
        await this.doUpload();
    };

    private resetState = async () => {
        this.setState(this.getInitialState())
    };

    private divider = () => {
        return <Divider style={{marginBottom: "0.25rem", marginTop: "0.25rem"}}/>
    }

    render() {
        const {activeStep, selectedDate, files, minDate, maxDate} = this.state;
        return (
                <>
                    <LoaderWithCompanySelected loading={false}>
                        <Stepper orientation="vertical" activeStep={activeStep}>
                            <Step>
                                <StepLabel>
                                    <Typography variant="h6">
                                        <Trans i18nKey="documents.upload.time_frame">Time frame</Trans>
                                    </Typography>
                                </StepLabel>
                                <StepContent>
                                    <Typography variant="subtitle2">
                                        <Trans i18nKey="documents.upload.time_frame_hint">
                                            Please select month and year you uploading documents for
                                        </Trans>
                                    </Typography>
                                    <Grid container>
                                        <Grid item>
                                            <MonthPicker
                                                    date={selectedDate}
                                                    minDate={minDate}
                                                    maxDate={maxDate}
                                                    onChange={date => {
                                                        this.setState({selectedDate: date!!})
                                                    }}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <YearPicker
                                                    date={selectedDate}
                                                    isDateDisabled={() => false}
                                                    minDate={minDate}
                                                    maxDate={maxDate}
                                                    onChange={date => {
                                                        if (date!! > maxDate) {
                                                            this.setState({selectedDate: maxDate})
                                                        } else if (date!! < minDate) {
                                                            this.setState({selectedDate: minDate})
                                                        } else {
                                                            this.setState({selectedDate: date!!})
                                                        }
                                                    }}
                                            />
                                        </Grid>
                                    </Grid>
                                    {this.divider()}
                                    <div>
                                        <div>
                                            <Button disabled><Trans i18nKey="common.back">Back</Trans></Button>
                                            <Button variant="contained" color="primary"
                                                    onClick={this.handleNext}><Trans i18nKey="common.next">Next</Trans></Button>
                                        </div>
                                    </div>
                                </StepContent>
                            </Step>
                            <Step>
                                <StepLabel>
                                    <Typography variant="h6">
                                        <Trans i18nKey="documents.upload.files.header">Files</Trans>
                                    </Typography>
                                </StepLabel>
                                <StepContent>
                                    <Translation>
                                        {t => <DropzoneArea
                                                maxFileSize={4500000} //4.5MB
                                                dropzoneText={t("common.dropzone", "Drag and drop a file here or click")}
                                                filesLimit={100}
                                                showPreviews={false}
                                                showPreviewsInDropzone={true}
                                                showAlerts={['error']}
                                                useChipsForPreview={true}
                                                showFileNamesInPreview={true}
                                                initialFiles={files}
                                                onChange={(files) => {
                                                    this.setState({files: files})
                                                }}
                                                onDropRejected={files => {
                                                    const newRejected = files
                                                            .map(it => it.name)
                                                            .concat(Array.from(this.state.rejectedFiles));
                                                    this.setState({
                                                        rejectedFiles: new Set(newRejected)
                                                    })
                                                }}
                                        />
                                        }
                                    </Translation>
                                    <Typography variant="subtitle1">
                                        <Trans i18nKey="documents.upload.files.title">
                                            Please upload the following documents:
                                        </Trans>
                                    </Typography>
                                    <ol>
                                        <li>
                                            <Typography variant="subtitle2">
                                                <Trans i18nKey="documents.upload.files.sales_invoices">
                                                    Sales invoices in <u>PDF</u>, in case you don't use our invoice generator
                                                </Trans>
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography variant="subtitle2">
                                                <Trans i18nKey="documents.upload.files.purchase_invoices">
                                                    Purchase invoices and receipts in <u>PDF</u>
                                                </Trans>
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography variant="subtitle2">
                                                <Trans i18nKey="documents.upload.files.statements">
                                                    Bank statements, <u>if you haven't given us access</u>
                                                </Trans>
                                            </Typography>
                                            <ul>
                                                <li>
                                                    <Typography variant="body2">
                                                        <Trans i18nKey="documents.upload.files.statements_est_banks">
                                                            in <u><b>XML</b></u> and <u><b>PDF</b></u>,
                                                            if you use an Estonian bank services
                                                        </Trans>
                                                    </Typography>
                                                </li>
                                                <li>
                                                    <Typography variant="body2">
                                                        <Trans i18nKey="documents.upload.files.statements_other_banks">
                                                            in <u><b>CSV</b></u> and <u><b>PDF</b></u>,
                                                            if you use TransferWise, EuroWallet, Revolut, PaySera, PayPal or
                                                            another payment system services
                                                        </Trans>
                                                    </Typography>
                                                </li>
                                            </ul>
                                        </li>
                                    </ol>
                                    {this.divider()}
                                    <div>
                                        <div>
                                            <Button onClick={this.handleBack}><Trans i18nKey="common.back">Back</Trans></Button>
                                            <Button disabled={files && files.length === 0} variant="contained" color="primary" onClick={this.handleNext}>
                                                <Trans i18nKey="common.next">Next</Trans>
                                            </Button>
                                        </div>
                                    </div>
                                </StepContent>
                            </Step>
                            <Step>
                                <StepLabel><Typography variant="h6"><Trans i18nKey="documents.upload.review.header">Review</Trans></Typography></StepLabel>
                                <StepContent>
                                    {this.renderReviewContent()}
                                    {this.divider()}
                                    <div>
                                        <div>
                                            <Button onClick={this.handleBack}><Trans i18nKey="common.back">Back</Trans></Button>
                                            <Button variant="contained" color="secondary" onClick={this.handleUploadStart}>
                                                <Trans i18nKey="common.upload">Upload</Trans>
                                            </Button>
                                        </div>
                                    </div>
                                </StepContent>
                            </Step>
                            <Step>
                                <StepLabel><Typography variant="h6"><Trans i18nKey="documents.upload.upload.header">Upload</Trans></Typography></StepLabel>
                                <StepContent>
                                    <FileUploadProgress ref={this.progressComponentRef}/>
                                    {this.divider()}
                                    <div>
                                        <div>
                                            <Button disabled>
                                                <Trans i18nKey="common.back">Back</Trans>
                                            </Button>
                                            <Button variant="contained" disabled={!this.state.uploadFinished} color="primary" onClick={this.resetState}>
                                                <Trans i18nKey="common.close">Close</Trans>
                                            </Button>
                                        </div>
                                    </div>
                                </StepContent>
                            </Step>
                        </Stepper>
                    </LoaderWithCompanySelected>
                </>
        );
    }

    private renderReviewContent() {
        const {selectedDate, files, rejectedFiles} = this.state;
        return <>
            <Typography variant="h5">
                <Trans i18nKey="documents.upload.review.period_summary">
                    I'm uploading documents for
                </Trans> <b>{format(selectedDate, 'MMMM')} ({format(selectedDate, 'MM')}) - {selectedDate.year()}</b>
            </Typography>
            {rejectedFiles.size > 0 && <Alert severity="error">
                <AlertTitle>
                    <Trans i18nKey="documents.upload.review.rejected_documents">
                        Following documents won't be uploaded:
                    </Trans>
                </AlertTitle>
                <List dense>
                    {Array.from(rejectedFiles).map((file, index) =>
                            <ListItem key={file}>
                                <ListItemAvatar>
                                    <Avatar>
                                        <FolderIcon/>
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText primary={index + 1 + ". " + file}/>
                            </ListItem>
                    )}
                </List>
            </Alert>
            }
            <Typography variant="h6">
                <Trans i18nKey="documents.upload.review.documents_to_upload">
                    Documents list:
                </Trans>
            </Typography>
            <List dense>
                {Array.from(files).map((file, index) =>
                        <ListItem key={file.name}>
                            <ListItemAvatar>
                                <Avatar>
                                    <FolderIcon/>
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText primary={index + 1 + ". " + file.name}/>
                        </ListItem>
                )}
            </List>
            <Divider/>
            <Translation>
                {t => <TextField
                        id="comment"
                        label={t("documents.upload.review.comment_label", "Comment for the accountant (optional)")}
                        placeholder={t("documents.upload.review.comment_placeholder", "Please leave a comment related to the documents you are uploading")}
                        multiline
                        value={this.state.comment}
                        fullWidth
                        rows={6}
                        maxRows={12}
                        onChange={e => this.setState({comment: e.target.value})}
                />}
            </Translation>

        </>;
    }

    private awaitForRef = async () => {
        let counter = 0;

        while (counter < 3) {
            if (!this.progressComponentRef.current) {
                await sleep(3000)
            } else {
                return this.progressComponentRef.current;
            }
            counter += 1;
        }
        throw new Error("Failed to wait for ref")
    }

    private doUpload = async () => {
        logEvent("doc_upload", "Starting document upload");
        const {selectedDate, files, comment} = this.state;
        const sessionId = await documentUploadApi.startUpload(selectedDate.month(), selectedDate.year(), comment, files.map(it => it.name));
        logEvent("doc_upload", `Document upload session ${sessionId} created`);

        const ref = await this.awaitForRef();
        const uploadDone = await ref.doUpload(sessionId, files.map(file => ({file})))
        if (uploadDone) {
            await documentUploadApi.finishUpload(sessionId)
            logEvent("doc_upload", `Session ${sessionId} is marked as finished`);
        }
        this.setState({
            uploadFinished: true
        })
    };
}